<template>
  <div class="holiday-search" ref="searchRoot">
    <!-- Search Bar -->
    <div class="search-bar" :class="{ focused: isFocused, 'has-filters': activeFilters.length > 0 }">
      <!-- Active filter chips -->
      <div class="filter-chips" v-if="activeFilters.length > 0">
        <span
          v-for="(filter, idx) in activeFilters"
          :key="idx"
          class="filter-chip"
          :class="`chip-${filter.type}`"
        >
          <i :class="chipIcon(filter.type)"></i>
          {{ filter.label }}
          <button class="chip-remove" @click.stop="removeFilter(idx)" aria-label="Remove filter">
            <i class="fas fa-times"></i>
          </button>
        </span>
      </div>

      <!-- Input area -->
      <div class="search-input-wrapper">
        <i class="fas fa-search search-icon"></i>
        <input
          ref="searchInput"
          type="text"
          v-model="query"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
          @keydown.down.prevent="onArrowDown"
          @keydown.up.prevent="onArrowUp"
          @keydown.enter.prevent="onEnter"
          @keydown.escape="onEscape"
          @keydown.backspace="onBackspace"
          class="search-field"
          :placeholder="searchPlaceholder"
          autocomplete="off"
          spellcheck="false"
        />
        <transition name="fade-fast">
          <button v-if="query || activeFilters.length > 0" class="clear-btn" @click.stop="clearAll" aria-label="Clear search">
            <i class="fas fa-times-circle"></i>
          </button>
        </transition>
        <div class="search-shortcut" v-if="!isFocused && !query && activeFilters.length === 0">
          <kbd>/</kbd>
        </div>
      </div>
    </div>

    <!-- Dropdown -->
    <transition name="dropdown-slide">
      <div v-if="showDropdown" class="search-dropdown" ref="dropdown">
        <!-- Quick Filters -->
        <div class="dropdown-section" v-if="!query && suggestedFilters.length > 0">
          <div class="section-label">
            <i class="fas fa-sliders-h"></i>
            Quick Filters
          </div>
          <div class="quick-filters">
            <button
              v-for="(filter, idx) in suggestedFilters"
              :key="'qf-' + idx"
              class="quick-filter-btn"
              :class="{ active: isFilterActive(filter) }"
              @mousedown.prevent="toggleQuickFilter(filter)"
            >
              <i :class="filter.icon"></i>
              {{ filter.label }}
            </button>
          </div>
        </div>

        <!-- Search Suggestions / Results -->
        <div class="dropdown-section" v-if="query && suggestions.length > 0">
          <div class="section-label">
            <i class="fas fa-lightbulb"></i>
            Suggestions
            <span class="results-count">{{ suggestions.length }} result{{ suggestions.length !== 1 ? 's' : '' }}</span>
          </div>
          <ul class="suggestions-list" ref="suggestionsList">
            <li
              v-for="(item, idx) in suggestions"
              :key="'s-' + idx"
              class="suggestion-item"
              :class="{ highlighted: idx === highlightedIndex }"
              @mousedown.prevent="selectSuggestion(item)"
              @mouseenter="highlightedIndex = idx"
            >
              <div class="suggestion-icon">
                <i class="fas fa-paper-plane"></i>
              </div>
              <div class="suggestion-content">
                <div class="suggestion-name" v-html="highlightMatch(item.holiday_name)"></div>
                <div class="suggestion-meta">
                  <span class="meta-item" v-if="item.company_name">
                    <i class="fas fa-building"></i> {{ item.company_name }}
                  </span>
                  <span class="meta-item" v-if="item.holiday_duration">
                    <i class="fas fa-clock"></i> {{ item.holiday_duration }} days
                  </span>
                  <span class="meta-item" :class="getStatusClass(item)">
                    <i class="fas fa-circle" style="font-size: 6px; vertical-align: middle;"></i>
                    {{ getStatusLabel(item) }}
                  </span>
                </div>
              </div>
              <div class="suggestion-price" v-if="getPrice(item)">
                {{ getPrice(item) }}
              </div>
            </li>
          </ul>
        </div>

        <!-- No Results -->
        <div class="dropdown-section no-results" v-if="query && suggestions.length === 0 && !isSearching">
          <div class="no-results-content">
            <i class="fas fa-search"></i>
            <p>No holidays found for "<strong>{{ query }}</strong>"</p>
            <span>Try adjusting your search or filters</span>
          </div>
        </div>

        <!-- Searching indicator -->
        <div class="dropdown-section searching" v-if="isSearching">
          <div class="searching-content">
            <div class="search-dots">
              <span></span><span></span><span></span>
            </div>
            <p>Searching...</p>
          </div>
        </div>

        <!-- Recent Searches -->
        <div class="dropdown-section" v-if="!query && recentSearches.length > 0">
          <div class="section-label">
            <i class="fas fa-history"></i>
            Recent Searches
            <button class="clear-recent" @mousedown.prevent="clearRecent">Clear</button>
          </div>
          <ul class="suggestions-list recent-list">
            <li
              v-for="(term, idx) in recentSearches"
              :key="'r-' + idx"
              class="suggestion-item recent-item"
              @mousedown.prevent="applyRecentSearch(term)"
            >
              <div class="suggestion-icon recent-icon">
                <i class="fas fa-clock"></i>
              </div>
              <span class="recent-text">{{ term }}</span>
              <button class="remove-recent" @mousedown.prevent.stop="removeRecentSearch(idx)">
                <i class="fas fa-times"></i>
              </button>
            </li>
          </ul>
        </div>

        <!-- Footer -->
        <div class="dropdown-footer" v-if="showDropdown">
          <span><kbd>↑</kbd> <kbd>↓</kbd> Navigate</span>
          <span><kbd>Enter</kbd> Select</span>
          <span><kbd>Esc</kbd> Close</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'HolidaySearch',
  props: {
    holidays: {
      type: Array,
      default: () => []
    },
    operators: {
      type: Array,
      default: () => []
    }
  },
  emits: ['search', 'filter-change'],
  data () {
    return {
      query: '',
      isFocused: false,
      showDropdown: false,
      highlightedIndex: -1,
      isSearching: false,
      activeFilters: [],
      recentSearches: [],
      suggestions: [],
      suggestedFilters: [
        { type: 'status', value: 'live', label: 'Live', icon: 'fas fa-circle text-live' },
        { type: 'status', value: 'draft', label: 'Draft', icon: 'fas fa-circle text-draft' },
        { type: 'duration', value: 'short', label: '1-3 Days', icon: 'fas fa-sun' },
        { type: 'duration', value: 'medium', label: '4-7 Days', icon: 'fas fa-umbrella-beach' },
        { type: 'duration', value: 'long', label: '8+ Days', icon: 'fas fa-globe-americas' }
      ]
    }
  },
  computed: {
    searchPlaceholder () {
      if (this.activeFilters.length > 0) {
        return 'Refine search...'
      }
      return 'Search holidays by name, operator, date...'
    },
    uniqueOperators () {
      const ops = [...new Set(this.holidays.map(h => h.company_name).filter(Boolean))]
      return ops.map(name => ({
        type: 'operator',
        value: name,
        label: name,
        icon: 'fas fa-building'
      }))
    }
  },
  watch: {
    holidays () {
      if (this.query) {
        this.performSearch()
      }
    }
  },
  methods: {
    // ── Search Logic ──
    onInput () {
      this.isSearching = true
      this.highlightedIndex = -1
      this.debouncedSearch()
    },
    debouncedSearch: _.debounce(function () {
      this.performSearch()
    }, 250),
    performSearch () {
      const q = this.query.toLowerCase().trim()

      if (!q) {
        this.suggestions = []
        this.isSearching = false
        this.emitFilteredResults()
        return
      }

      const filtered = this.holidays.filter(holiday => {
        // text match
        const textMatch = this.matchesText(holiday, q)

        // also apply active filters
        const filterMatch = this.matchesFilters(holiday)

        return textMatch && filterMatch
      })

      this.suggestions = filtered.slice(0, 8)
      this.isSearching = false
      this.emitFilteredResults()
    },
    matchesText (holiday, q) {
      const fields = [
        holiday.holiday_name,
        holiday.company_name,
        String(holiday.holiday_duration),
        holiday.holiday_currency
      ]

      // Also search in instances
      if (holiday.instances && holiday.instances.length > 0) {
        holiday.instances.forEach(inst => {
          if (inst.date_start) fields.push(inst.date_start)
          if (inst.reference) fields.push(inst.reference)
          if (inst.price !== undefined) fields.push(String(inst.price))
        })
      }

      return fields.some(field =>
        field && field.toString().toLowerCase().includes(q)
      )
    },
    matchesFilters (holiday) {
      if (this.activeFilters.length === 0) return true

      return this.activeFilters.every(filter => {
        switch (filter.type) {
          case 'status': {
            const isLive = holiday.instances &&
              holiday.instances.some(inst => inst.status === 1)
            const isDraft = holiday.instances &&
              holiday.instances.some(inst => inst.status === 0 || inst.status === '0')
            if (filter.value === 'live') return isLive
            if (filter.value === 'draft') return isDraft
            return true
          }
          case 'duration': {
            const days = Number(holiday.holiday_duration) || 0
            if (filter.value === 'short') return days >= 1 && days <= 3
            if (filter.value === 'medium') return days >= 4 && days <= 7
            if (filter.value === 'long') return days >= 8
            return true
          }
          case 'operator':
            return holiday.company_name === filter.value
          default:
            return true
        }
      })
    },
    emitFilteredResults () {
      const q = this.query.toLowerCase().trim()
      let result = this.holidays

      if (q) {
        result = result.filter(h => this.matchesText(h, q))
      }

      result = result.filter(h => this.matchesFilters(h))

      this.$emit('search', result)
      this.$emit('filter-change', {
        query: this.query,
        filters: [...this.activeFilters],
        resultCount: result.length
      })
    },

    // ── UI Events ──
    onFocus () {
      this.isFocused = true
      this.showDropdown = true
    },
    onBlur () {
      // Delay to allow click events on dropdown
      setTimeout(() => {
        this.isFocused = false
        this.showDropdown = false
        this.highlightedIndex = -1
      }, 200)
    },
    onEscape () {
      this.$refs.searchInput.blur()
      this.showDropdown = false
    },
    onArrowDown () {
      const max = this.suggestions.length - 1
      if (this.highlightedIndex < max) {
        this.highlightedIndex++
        this.scrollToHighlighted()
      }
    },
    onArrowUp () {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--
        this.scrollToHighlighted()
      }
    },
    onEnter () {
      if (this.highlightedIndex >= 0 && this.suggestions[this.highlightedIndex]) {
        this.selectSuggestion(this.suggestions[this.highlightedIndex])
      } else if (this.query) {
        this.addToRecent(this.query)
        this.showDropdown = false
        this.$refs.searchInput.blur()
      }
    },
    onBackspace () {
      if (!this.query && this.activeFilters.length > 0) {
        this.activeFilters.pop()
        this.emitFilteredResults()
      }
    },
    scrollToHighlighted () {
      this.$nextTick(() => {
        const list = this.$refs.suggestionsList
        if (list) {
          const item = list.children[this.highlightedIndex]
          if (item) {
            item.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          }
        }
      })
    },

    // ── Selection & Filters ──
    selectSuggestion (item) {
      this.query = item.holiday_name
      this.addToRecent(item.holiday_name)
      this.showDropdown = false
      this.performSearch()
    },
    toggleQuickFilter (filter) {
      const existingIdx = this.activeFilters.findIndex(
        f => f.type === filter.type && f.value === filter.value
      )
      if (existingIdx > -1) {
        this.activeFilters.splice(existingIdx, 1)
      } else {
        // Remove same-type filters for exclusive types
        if (filter.type === 'status' || filter.type === 'duration') {
          this.activeFilters = this.activeFilters.filter(f => f.type !== filter.type)
        }
        this.activeFilters.push({ ...filter })
      }
      this.performSearch()
      this.emitFilteredResults()
    },
    removeFilter (idx) {
      this.activeFilters.splice(idx, 1)
      this.performSearch()
      this.emitFilteredResults()
    },
    isFilterActive (filter) {
      return this.activeFilters.some(
        f => f.type === filter.type && f.value === filter.value
      )
    },
    clearAll () {
      this.query = ''
      this.activeFilters = []
      this.suggestions = []
      this.highlightedIndex = -1
      this.emitFilteredResults()
      this.$refs.searchInput.focus()
    },

    // ── Recent Searches ──
    addToRecent (term) {
      if (!term || term.trim() === '') return
      this.recentSearches = this.recentSearches.filter(t => t !== term)
      this.recentSearches.unshift(term)
      if (this.recentSearches.length > 5) {
        this.recentSearches = this.recentSearches.slice(0, 5)
      }
      this.saveRecent()
    },
    applyRecentSearch (term) {
      this.query = term
      this.performSearch()
      this.showDropdown = true
    },
    removeRecentSearch (idx) {
      this.recentSearches.splice(idx, 1)
      this.saveRecent()
    },
    clearRecent () {
      this.recentSearches = []
      this.saveRecent()
    },
    saveRecent () {
      try {
        localStorage.setItem('sf_holiday_recent_searches', JSON.stringify(this.recentSearches))
      } catch (e) {
        // localStorage not available
      }
    },
    loadRecent () {
      try {
        const data = localStorage.getItem('sf_holiday_recent_searches')
        if (data) {
          this.recentSearches = JSON.parse(data)
        }
      } catch (e) {
        this.recentSearches = []
      }
    },

    // ── Helpers ──
    highlightMatch (text) {
      if (!text || !this.query) return text
      const q = this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${q})`, 'gi')
      return text.replace(regex, '<mark>$1</mark>')
    },
    getStatusLabel (holiday) {
      if (!holiday.instances || holiday.instances.length === 0) return 'No instances'
      const liveCount = holiday.instances.filter(i => i.status === 1).length
      if (liveCount === holiday.instances.length) return 'Live'
      if (liveCount === 0) return 'Draft'
      return `${liveCount}/${holiday.instances.length} Live`
    },
    getStatusClass (holiday) {
      if (!holiday.instances || holiday.instances.length === 0) return 'status-none'
      const liveCount = holiday.instances.filter(i => i.status === 1).length
      if (liveCount === holiday.instances.length) return 'status-live'
      if (liveCount === 0) return 'status-draft'
      return 'status-mixed'
    },
    getPrice (holiday) {
      if (!holiday.instances || holiday.instances.length === 0) return null
      const prices = holiday.instances.map(i => i.price).filter(p => p !== undefined && p !== null)
      if (prices.length === 0) return null
      const symbol = this.getCurrencySymbol(holiday.holiday_currency)
      const minPrice = Math.min(...prices)
      if (prices.length > 1) {
        const maxPrice = Math.max(...prices)
        if (minPrice !== maxPrice) return `${symbol}${minPrice} - ${symbol}${maxPrice}`
      }
      return `${symbol}${minPrice}`
    },
    getCurrencySymbol (currency) {
      if (!currency) return ''
      switch (currency.toLowerCase()) {
        case 'gbp': return '£'
        case 'usd': return '$'
        case 'eur': return '€'
        default: return currency
      }
    },
    chipIcon (type) {
      switch (type) {
        case 'status': return 'fas fa-circle'
        case 'duration': return 'fas fa-clock'
        case 'operator': return 'fas fa-building'
        default: return 'fas fa-tag'
      }
    },
    handleGlobalKeydown (e) {
      // '/' shortcut to focus search
      if (e.key === '/' && !this.isFocused) {
        const tagName = document.activeElement?.tagName?.toLowerCase()
        if (tagName !== 'input' && tagName !== 'textarea' && tagName !== 'select') {
          e.preventDefault()
          this.$refs.searchInput.focus()
        }
      }
    },
    handleClickOutside (e) {
      if (this.$refs.searchRoot && !this.$refs.searchRoot.contains(e.target)) {
        this.showDropdown = false
        this.isFocused = false
      }
    }
  },
  mounted () {
    this.loadRecent()
    document.addEventListener('keydown', this.handleGlobalKeydown)
    document.addEventListener('mousedown', this.handleClickOutside)
  },
  beforeUnmount () {
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    document.removeEventListener('mousedown', this.handleClickOutside)
  }
}
</script>

<style lang="scss" scoped>
// ── Variables ──
$primary: #614fe8;
$primary-light: rgba(97, 79, 232, 0.08);
$primary-lighter: rgba(97, 79, 232, 0.04);
$border-color: #e2e8f0;
$border-focus: #614fe8;
$text-primary: #1a202c;
$text-secondary: #718096;
$text-muted: #a0aec0;
$bg-dropdown: #ffffff;
$bg-hover: #f7fafc;
$bg-highlighted: rgba(97, 79, 232, 0.06);
$live-color: #60CE5B;
$draft-color: #EF6565;
$shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06);
$shadow-md: 0 4px 20px rgba(0, 0, 0, 0.08);
$shadow-lg: 0 10px 40px rgba(0, 0, 0, 0.12);
$radius: 12px;
$radius-sm: 8px;
$transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

.holiday-search {
  position: relative;
  width: 100%;
  max-width: 520px;
  z-index: 100;
}

// ── Search Bar ──
.search-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background: #ffffff;
  border: 1.5px solid $border-color;
  border-radius: $radius;
  transition: $transition;
  overflow: hidden;
  min-height: 48px;

  &:hover {
    border-color: darken($border-color, 8%);
    box-shadow: $shadow-sm;
  }

  &.focused {
    border-color: $border-focus;
    box-shadow: 0 0 0 3px rgba($primary, 0.1), $shadow-md;
  }

  &.has-filters {
    padding-left: 8px;
    padding-top: 4px;
    padding-bottom: 4px;
  }
}

// ── Filter Chips ──
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 2px 0;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  color: $primary;
  background: $primary-light;
  white-space: nowrap;
  animation: chipIn 0.2s ease-out;

  i {
    font-size: 6px;
    vertical-align: middle;
  }

  &.chip-status i { color: $live-color; }
  &.chip-status .chip-remove ~ i { color: $draft-color; }
  &.chip-duration { background: rgba(#f6ad55, 0.12); color: #c05621; }
  &.chip-duration i { font-size: 10px; }
  &.chip-operator { background: rgba(#4299e1, 0.1); color: #2b6cb0; }
  &.chip-operator i { font-size: 10px; }
}

.chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: inherit;
  opacity: 0.5;
  transition: $transition;
  border-radius: 50%;

  i { font-size: 8px; }

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.08);
  }
}

// ── Input ──
.search-input-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 180px;
  position: relative;
}

.search-icon {
  color: $text-muted;
  font-size: 13px;
  margin-left: 14px;
  transition: color 0.2s;
  flex-shrink: 0;

  .focused & {
    color: $primary;
  }
}

.search-field {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 10px;
  font-size: 13.5px;
  color: $text-primary;
  background: transparent;
  font-weight: 500;
  letter-spacing: -0.01em;

  &::placeholder {
    color: $text-muted;
    font-weight: 400;
  }
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  padding: 6px;
  margin-right: 8px;
  border-radius: 50%;
  transition: $transition;

  i { font-size: 14px; }

  &:hover {
    color: $draft-color;
    background: rgba($draft-color, 0.08);
  }
}

.search-shortcut {
  margin-right: 12px;
  flex-shrink: 0;

  kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    font-size: 11px;
    font-family: inherit;
    font-weight: 600;
    color: $text-muted;
    background: #f7fafc;
    border: 1px solid $border-color;
    border-radius: 4px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
  }
}

// ── Dropdown ──
.search-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: $bg-dropdown;
  border: 1px solid $border-color;
  border-radius: $radius;
  box-shadow: $shadow-lg;
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 200;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: darken($border-color, 5%);
    border-radius: 10px;
  }
}

.dropdown-section {
  padding: 8px 6px;

  & + & {
    border-top: 1px solid rgba($border-color, 0.7);
  }
}

.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 8px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $text-muted;

  i {
    font-size: 10px;
  }

  .results-count {
    margin-left: auto;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: $text-secondary;
    font-size: 11px;
  }

  .clear-recent {
    margin-left: auto;
    border: none;
    background: transparent;
    color: $primary;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    text-transform: none;
    letter-spacing: 0;
    padding: 2px 6px;
    border-radius: 4px;

    &:hover {
      background: $primary-light;
    }
  }
}

// ── Quick Filters ──
.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 6px 4px;
}

.quick-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: $text-secondary;
  background: #f7fafc;
  border: 1px solid $border-color;
  border-radius: 20px;
  cursor: pointer;
  transition: $transition;
  white-space: nowrap;

  i {
    font-size: 8px;
  }

  .text-live { color: $live-color; }
  .text-draft { color: $draft-color; }

  &:hover {
    border-color: $primary;
    color: $primary;
    background: $primary-lighter;
  }

  &.active {
    border-color: $primary;
    background: $primary-light;
    color: $primary;
    font-weight: 600;
  }
}

// ── Suggestions List ──
.suggestions-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: $transition;

  &:hover,
  &.highlighted {
    background: $bg-highlighted;
  }

  &.highlighted {
    box-shadow: inset 3px 0 0 $primary;
  }
}

.suggestion-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: $primary-light;
  color: $primary;
  font-size: 13px;
  flex-shrink: 0;
}

.suggestion-content {
  flex: 1;
  min-width: 0;
}

.suggestion-name {
  font-size: 13px;
  font-weight: 600;
  color: $text-primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  :deep(mark) {
    background: rgba($primary, 0.15);
    color: $primary;
    font-weight: 700;
    padding: 0 1px;
    border-radius: 2px;
  }
}

.suggestion-meta {
  display: flex;
  gap: 10px;
  margin-top: 3px;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: $text-secondary;

  i { font-size: 9px; }

  &.status-live { color: $live-color; }
  &.status-draft { color: $draft-color; }
  &.status-mixed { color: #f6ad55; }
  &.status-none { color: $text-muted; }
}

.suggestion-price {
  font-size: 13px;
  font-weight: 700;
  color: $text-primary;
  white-space: nowrap;
  flex-shrink: 0;
}

// ── Recent Searches ──
.recent-item {
  padding: 8px 12px;
}

.recent-icon {
  background: transparent !important;
  color: $text-muted !important;
  width: 24px;
  height: 24px;
  font-size: 11px;
}

.recent-text {
  flex: 1;
  font-size: 13px;
  color: $text-secondary;
}

.remove-recent {
  display: none;
  border: none;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    color: $draft-color;
    background: rgba($draft-color, 0.08);
  }

  .suggestion-item:hover & {
    display: flex;
  }
}

// ── No Results / Searching ──
.no-results-content,
.searching-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  color: $text-muted;

  i {
    font-size: 24px;
    margin-bottom: 10px;
    opacity: 0.4;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: $text-secondary;

    strong { color: $text-primary; }
  }

  span {
    font-size: 11px;
    margin-top: 4px;
  }
}

// ── Search Dots Animation ──
.search-dots {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;

  span {
    width: 6px;
    height: 6px;
    background: $primary;
    border-radius: 50%;
    animation: dotPulse 1s ease-in-out infinite;

    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
}

@keyframes dotPulse {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}

// ── Footer ──
.dropdown-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 8px 12px;
  border-top: 1px solid rgba($border-color, 0.7);
  background: #fafbfc;
  border-radius: 0 0 $radius $radius;

  span {
    font-size: 10.5px;
    color: $text-muted;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 17px;
    padding: 0 4px;
    font-size: 9.5px;
    font-family: inherit;
    font-weight: 600;
    color: $text-muted;
    background: #ffffff;
    border: 1px solid $border-color;
    border-radius: 3px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
  }
}

// ── Transitions ──
.dropdown-slide-enter-active {
  animation: dropdownIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.dropdown-slide-leave-active {
  animation: dropdownOut 0.15s ease-in;
}

@keyframes dropdownIn {
  0% {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes dropdownOut {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.15s;
}
.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}

@keyframes chipIn {
  0% {
    opacity: 0;
    transform: scale(0.85);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
